import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Upload,
  Download,
  ArrowRight,
  ArrowLeft,
  CheckCircle2,
  AlertCircle,
  FileSpreadsheet,
  Loader2,
} from 'lucide-react';
import { toast } from 'sonner';
import { categoriesImportApi } from '../../services/api';

type ParsedRow = {
  rowNumber: number;
  raw: Record<string, string>;
  errors: string[];
  dto?: unknown;
};
type ParseResult = {
  totalRows: number;
  validCount: number;
  invalidCount: number;
  rows: ParsedRow[];
};

type Step = 1 | 2 | 3;

export function CategoriesImport() {
  const navigate = useNavigate();

  const [step, setStep] = useState<Step>(1);

  const [file, setFile] = useState<File | null>(null);
  const [dragOver, setDragOver] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [downloadingTemplate, setDownloadingTemplate] = useState(false);

  const [previewing, setPreviewing] = useState(false);
  const [preview, setPreview] = useState<ParseResult | null>(null);

  const [committing, setCommitting] = useState(false);
  const [commitResult, setCommitResult] = useState<{
    importedCount: number;
    skippedCount: number;
  } | null>(null);

  const onPickFile = (f: File | null) => {
    if (!f) return;
    if (!f.name.toLowerCase().endsWith('.xlsx')) {
      toast.error('Seuls les fichiers .xlsx sont acceptés');
      return;
    }
    setFile(f);
    setPreview(null);
  };

  const handlePreview = async () => {
    if (!file) return;
    setPreviewing(true);
    try {
      const { data } = await categoriesImportApi.preview(file);
      setPreview(data);
      setStep(2);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Échec de l'analyse du fichier");
    } finally {
      setPreviewing(false);
    }
  };

  const handleCommit = async () => {
    if (!file) return;
    setCommitting(true);
    try {
      const { data } = await categoriesImportApi.commit(file);
      setCommitResult({ importedCount: data.importedCount, skippedCount: data.skippedCount });
      setStep(3);
      toast.success(`${data.importedCount} catégorie(s) importée(s)`);
    } catch (err: any) {
      toast.error(err?.response?.data?.message || "Échec de l'import");
    } finally {
      setCommitting(false);
    }
  };

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
          Importer des catégories
        </h1>
        <p className="text-gray-500 dark:text-gray-400">
          Téléchargez le modèle Excel, remplissez-le et importez vos catégories en masse.
        </p>
      </div>

      <Stepper step={step} />

      <div className="bg-white dark:bg-gray-800 rounded-xl shadow-sm p-6">
        {step === 1 && (
          <StepUpload
            file={file}
            dragOver={dragOver}
            setDragOver={setDragOver}
            fileInputRef={fileInputRef}
            onPickFile={onPickFile}
            downloadingTemplate={downloadingTemplate}
            onDownloadTemplate={async () => {
              setDownloadingTemplate(true);
              try {
                await categoriesImportApi.downloadTemplate('fr');
              } catch {
                toast.error('Impossible de télécharger le modèle');
              } finally {
                setDownloadingTemplate(false);
              }
            }}
            onPreview={handlePreview}
            previewing={previewing}
          />
        )}

        {step === 2 && preview && (
          <StepPreview
            preview={preview}
            onBack={() => setStep(1)}
            onCommit={handleCommit}
            committing={committing}
          />
        )}

        {step === 3 && commitResult && (
          <StepDone
            importedCount={commitResult.importedCount}
            skippedCount={commitResult.skippedCount}
            onSeeList={() => navigate('/admin/categories')}
            onAnother={() => {
              setStep(1);
              setFile(null);
              setPreview(null);
              setCommitResult(null);
            }}
          />
        )}
      </div>
    </div>
  );
}

function Stepper({ step }: { step: Step }) {
  const labels = ['Fichier', 'Aperçu', 'Terminé'];
  return (
    <div className="flex items-center gap-3">
      {labels.map((label, i) => {
        const idx = (i + 1) as Step;
        const active = idx === step;
        const done = idx < step;
        return (
          <div key={label} className="flex items-center gap-2">
            <div
              className={`w-8 h-8 rounded-full flex items-center justify-center text-sm font-semibold ${
                done
                  ? 'bg-emerald-600 text-white'
                  : active
                    ? 'bg-emerald-100 text-emerald-700 ring-2 ring-emerald-500'
                    : 'bg-gray-200 dark:bg-gray-700 text-gray-500'
              }`}
            >
              {done ? <CheckCircle2 size={16} /> : idx}
            </div>
            <span
              className={`text-sm ${
                active
                  ? 'text-gray-900 dark:text-white font-medium'
                  : 'text-gray-500 dark:text-gray-400'
              }`}
            >
              {label}
            </span>
            {i < labels.length - 1 && (
              <ArrowRight size={14} className="text-gray-300 dark:text-gray-600" />
            )}
          </div>
        );
      })}
    </div>
  );
}

function StepUpload(props: {
  file: File | null;
  dragOver: boolean;
  setDragOver: (v: boolean) => void;
  fileInputRef: React.RefObject<HTMLInputElement>;
  onPickFile: (f: File | null) => void;
  downloadingTemplate: boolean;
  onDownloadTemplate: () => void;
  onPreview: () => void;
  previewing: boolean;
}) {
  const {
    file,
    dragOver,
    setDragOver,
    fileInputRef,
    onPickFile,
    downloadingTemplate,
    onDownloadTemplate,
    onPreview,
    previewing,
  } = props;

  return (
    <div className="space-y-5">
      <div className="rounded-lg bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 p-4 text-sm text-blue-900 dark:text-blue-100">
        <strong>Colonnes du fichier :</strong> examSlug (obligatoire — slug de l'examen parent),
        name (obligatoire), slug (auto-généré si vide), nameEn, description, descriptionEn, icon, order
      </div>

      <div>
        <button
          type="button"
          onClick={onDownloadTemplate}
          disabled={downloadingTemplate}
          className="inline-flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg text-gray-700 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-700 disabled:opacity-50"
        >
          {downloadingTemplate ? <Loader2 size={16} className="animate-spin" /> : <Download size={16} />}
          Télécharger le modèle Excel
        </button>
      </div>

      <div
        onDragOver={(e) => {
          e.preventDefault();
          setDragOver(true);
        }}
        onDragLeave={() => setDragOver(false)}
        onDrop={(e) => {
          e.preventDefault();
          setDragOver(false);
          onPickFile(e.dataTransfer.files?.[0] || null);
        }}
        onClick={() => fileInputRef.current?.click()}
        className={`border-2 border-dashed rounded-xl p-8 text-center cursor-pointer transition-colors ${
          dragOver
            ? 'border-emerald-500 bg-emerald-50/60 dark:bg-emerald-900/20'
            : 'border-gray-300 dark:border-gray-600 hover:border-emerald-400'
        }`}
      >
        <input
          ref={fileInputRef}
          type="file"
          accept=".xlsx,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet"
          className="hidden"
          onChange={(e) => onPickFile(e.target.files?.[0] || null)}
        />
        {file ? (
          <div className="flex items-center justify-center gap-3 text-gray-800 dark:text-gray-100">
            <FileSpreadsheet size={28} className="text-emerald-600" />
            <div className="text-left">
              <p className="font-medium">{file.name}</p>
              <p className="text-xs text-gray-500">{(file.size / 1024).toFixed(1)} Ko</p>
            </div>
          </div>
        ) : (
          <div className="flex flex-col items-center gap-2 text-gray-500 dark:text-gray-400">
            <Upload size={32} />
            <p className="text-sm">Cliquez ou déposez un fichier .xlsx ici</p>
          </div>
        )}
      </div>

      <div className="flex justify-end">
        <button
          onClick={onPreview}
          disabled={!file || previewing}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {previewing ? <Loader2 size={16} className="animate-spin" /> : <ArrowRight size={16} />}
          Analyser le fichier
        </button>
      </div>
    </div>
  );
}

function StepPreview(props: {
  preview: ParseResult;
  onBack: () => void;
  onCommit: () => void;
  committing: boolean;
}) {
  const { preview, onBack, onCommit, committing } = props;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-3 gap-4">
        <StatCard label="Total" value={preview.totalRows} color="text-gray-700 dark:text-gray-200" />
        <StatCard label="Valides" value={preview.validCount} color="text-emerald-600" />
        <StatCard label="Invalides" value={preview.invalidCount} color="text-red-600" />
      </div>

      <div className="border border-gray-200 dark:border-gray-700 rounded-lg overflow-hidden">
        <div className="max-h-[480px] overflow-y-auto">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 dark:bg-gray-700/50 sticky top-0">
              <tr>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ligne</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Examen</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Nom</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Slug</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Ordre</th>
                <th className="px-3 py-2 text-left text-xs font-medium text-gray-500 uppercase">Statut</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 dark:divide-gray-700">
              {preview.rows.map((r) => (
                <tr
                  key={r.rowNumber}
                  className={r.errors.length > 0 ? 'bg-red-50 dark:bg-red-900/10' : ''}
                >
                  <td className="px-3 py-2 text-gray-500">{r.rowNumber}</td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono text-xs max-w-[140px] truncate">
                    {r.raw.examSlug || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-900 dark:text-gray-100 max-w-xs truncate">
                    {r.raw.name || <em className="text-gray-400">(vide)</em>}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300 font-mono text-xs">
                    {r.raw.slug || '—'}
                  </td>
                  <td className="px-3 py-2 text-gray-700 dark:text-gray-300">
                    {r.raw.order || '0'}
                  </td>
                  <td className="px-3 py-2">
                    {r.errors.length === 0 ? (
                      <span className="inline-flex items-center gap-1 text-emerald-600 text-xs">
                        <CheckCircle2 size={14} /> Valide
                      </span>
                    ) : (
                      <span className="inline-flex items-start gap-1 text-red-600 text-xs">
                        <AlertCircle size={14} className="shrink-0 mt-0.5" />
                        <span>{r.errors.join(' · ')}</span>
                      </span>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="flex justify-between">
        <button
          onClick={onBack}
          className="flex items-center gap-2 px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          <ArrowLeft size={16} />
          Retour
        </button>
        <button
          onClick={onCommit}
          disabled={preview.validCount === 0 || committing}
          className="flex items-center gap-2 px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700 disabled:opacity-50"
        >
          {committing ? <Loader2 size={16} className="animate-spin" /> : <CheckCircle2 size={16} />}
          Importer {preview.validCount} catégorie(s)
        </button>
      </div>
    </div>
  );
}

function StatCard({ label, value, color }: { label: string; value: number; color: string }) {
  return (
    <div className="rounded-lg border border-gray-200 dark:border-gray-700 p-4">
      <p className="text-xs text-gray-500 uppercase">{label}</p>
      <p className={`text-2xl font-bold ${color}`}>{value}</p>
    </div>
  );
}

function StepDone(props: {
  importedCount: number;
  skippedCount: number;
  onSeeList: () => void;
  onAnother: () => void;
}) {
  const { importedCount, skippedCount, onSeeList, onAnother } = props;
  return (
    <div className="text-center py-8 space-y-4">
      <div className="w-16 h-16 mx-auto rounded-full bg-emerald-100 dark:bg-emerald-900/30 flex items-center justify-center">
        <CheckCircle2 size={32} className="text-emerald-600" />
      </div>
      <h2 className="text-xl font-semibold text-gray-900 dark:text-white">
        Import terminé
      </h2>
      <p className="text-gray-600 dark:text-gray-300">
        {importedCount} catégorie(s) importée(s)
        {skippedCount > 0 && ` · ${skippedCount} ligne(s) ignorée(s)`}
      </p>
      <div className="flex justify-center gap-3">
        <button
          onClick={onAnother}
          className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-200 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
        >
          Importer un autre fichier
        </button>
        <button
          onClick={onSeeList}
          className="px-4 py-2 bg-emerald-600 text-white rounded-lg hover:bg-emerald-700"
        >
          Voir les catégories
        </button>
      </div>
    </div>
  );
}
